/*
 * 学校管理者プロファイル（schoolAdmins: 所属/職階/役割）共通関数
 * コンポーネントで使用するには mixins で読み込む
 *   import saf from '@/mixins/schoolAdminFunctions';
 *   mixins: [saf],
 *
 * サイト種別ごとに表示する schoolAdmins 列（labels.json schoolAdminFields が定義元・種別により列数可変:
 * school=所属/役職/役割の3列、company・university=所属/職階の2列）を引く分類・抽出を集約。
 * RegistSchool / UpdateSchool / SchoolList / Teacher/School で同一ロジックが重複していたため一元化（#3185）。
 * ※ labels は this.helper.master.labels から参照するため、利用側コンポーネントは helper を state に持つこと。
 *
 * 契約担当者（contractPersons）は別系統。呼称が組織種類によらず固定で役割を持たないため、
 * labels.json 非依存の固定 defs（contractPersonFieldDefs / contractPersonAttrs）を下に分けて置く。
 */
export default {
  methods: {
    /** サイト種別キー: is_company=1→company / 大学以上(school_type=4)→university / 他→school（フリースクール5含む） */
    schoolAdminFieldType(isCompany, schoolType) {
      if (Number(isCompany) === 1) return 'company';
      if (Number(schoolType) === 4) return 'university';
      return 'school';
    },
    /** サイト種別に応じた表示列の定義 [{name, label, placeholder}]（種別により列数可変）
     *  （labels.json schoolAdminFields 由来・物理列順 department→position→role で安定）
     *  accountType: 12（副管理者）のときのみ所属(department)の出し分けが効く。既定 11（主管理者）は全列。 */
    schoolAdminFieldDefs(isCompany, schoolType, accountType = 11) {
      const defs = this.helper && this.helper.master && this.helper.master.labels
        && this.helper.master.labels.schoolAdminFields;
      const type = this.schoolAdminFieldType(isCompany, schoolType);
      const group = defs && defs[type];
      if (!group) return [];
      // 副管理者(account_type=12)は専門・短大・大学/企業(fieldType !== 'school')のときだけ所属を表示する。
      // 小中高・フリースクール・個人(fieldType === 'school')の副管理者では所属欄を出さない。
      // 主管理者(account_type=11・既定)は全種別で所属を表示するため従来どおり全列を返す。
      const hideDepartment = Number(accountType) === 12 && type === 'school';
      return ['department', 'position', 'role']
        .filter((key) => group[key] && !(hideDepartment && key === 'department'))
        .map((key) => ({ name: key, label: group[key].label, placeholder: group[key].placeholder }));
    },
    /** 送信直前に呼ぶ: サイト種別で非表示の schoolAdmins 列（表示列に含まれない列）を target 上で null 化する。
     *  学校タイプ切替などで取り残された旧値を DB に登録/更新しないため（表示列のみ保存・隠れ列は常に null）。 */
    clearHiddenSchoolAdminFields(target, isCompany, schoolType, accountType = 11) {
      if (!target) return;
      const visible = this.schoolAdminFieldDefs(isCompany, schoolType, accountType).map((field) => field.name);
      // labels(master) 未配信などで表示列を確定できない時は何もしない（fail-safe）。
      // 全 site type は labels.json で必ず2列以上定義されるため visible=[] は「判定不能」のみを意味し、
      // ここで止めても正規の null 化は取りこぼさない。更新経路で既存値を黙って全消去するのを防ぐ。
      if (!visible.length) return;
      ['department', 'position', 'role'].forEach((key) => {
        if (!visible.includes(key)) target[key] = null;
      });
    },
    /** 契約担当者の表示・入力列の定義 [{name, label}]（固定 2 列）。
     *  管理者用の schoolAdminFieldDefs と分けているのは、契約担当者の呼称が
     *  組織種類（企業/大学/学校）によらず「部署」「職階」で固定であり、かつ「役割」を
     *  いかなる場合も持たないため。よって labels.json（種別ごとに列構成が変わる）にも
     *  helper.master の配信タイミングにも依存させない。管理者・副管理者行は従来どおり
     *  種別別ラベルと役割を出すので、そちらの経路は本メソッドの影響を受けない。
     *  表示 2 面（SchoolList / QR）と入力 2 画面（RegistSchool / UpdateSchool）の
     *  必須判定・alert 語彙がここを共有し、表示ゲートと必須チェックの軸が構造的に一致する。 */
    contractPersonFieldDefs() {
      return [
        { name: 'department', label: '部署' },
        { name: 'position', label: '職階' },
      ];
    },
    /** 契約担当者レコード（「管理者と同一」ON のときは代替表示元の管理者ピボット行）から
     *  表示用 {label, value} 配列を生成（空値は除外）。SchoolList / QR 共用。 */
    contractPersonAttrs(source) {
      if (!source) return [];
      return this.contractPersonFieldDefs()
        .map((f) => ({ label: f.label, value: source[f.name] }))
        .filter((x) => x.value && String(x.value).trim() !== '');
    },
  },
};
